import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Database, Cloud, Cpu, Brain, LayoutGrid, Package } from 'lucide-react';
import FadeInUp from '../components/FadeInUp';
import AnimatedCounter from '../components/AnimatedCounter';
import SEO from '../components/SEO';

const LinkedInIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
    <rect x="2" y="9" width="4" height="12"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

const heroWords = ['source', '·', 'prep', '·', 'serve'];

const Home = () => {
  const capabilities = [
    { icon: <Database className="w-8 h-8" />, title: 'Data Architecture', description: 'Enterprise-grade data platform design and implementation' },
    { icon: <Cloud className="w-8 h-8" />, title: 'Cloud Platforms', description: 'Azure, AWS, GCP migration and optimisation' },
    { icon: <Cpu className="w-8 h-8" />, title: 'Data Engineering', description: 'Spark, SQL, Python, Databricks, Snowflake pipelines' },
    { icon: <Brain className="w-8 h-8" />, title: 'AI/ML Enablement', description: 'Machine learning infrastructure and model deployment' },
    { icon: <LayoutGrid className="w-8 h-8" />, title: 'Platform Strategy', description: 'Roadmaps, governance, and operating model design' },
    { icon: <Package className="w-8 h-8" />, title: 'Productised Services', description: 'Fixed-scope engagements with defined outcomes' },
  ];

  const stats = [
    { value: 15, suffix: '+', label: 'Years Experience' },
    { value: 50, suffix: '+', label: 'Enterprise Engagements' },
    { value: 3, suffix: '', label: 'Major Cloud Platforms' },
    { value: 100, suffix: '%', label: 'Client Satisfaction' },
  ];

  const whyPoints = [
    { title: 'Enterprise Pedigree', description: 'Deep experience across ASX-listed corporations, government agencies, and global enterprises.' },
    { title: 'Technology Agnostic', description: 'We recommend the right tool for the job — not the one that pays the highest commission.' },
    { title: 'Outcome Obsessed', description: 'Every engagement is designed to deliver measurable, tangible business value.' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO
        description="Micro-consulting for the enterprise. Senior-led data architecture, cloud platforms, and AI enablement for Australia's largest organisations."
        path="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col justify-center px-8 md:px-16 lg:px-24 pt-32 pb-20">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://media.base44.com/images/public/69fc95317c6dac27dd24f467/cb73994ae_generated_76f4f679.png"
            alt="Abstract architectural precision"
            className="w-full h-full object-cover"
            fetchpriority="high"
          />
        </div>
        <div className="relative z-10 max-w-4xl">
          <motion.p
            className="text-xs md:text-sm tracking-[0.3em] uppercase text-silver mb-12"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Micro-Consulting for the Enterprise
          </motion.p>

          {/* Word reveal hero */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-light mb-8 leading-tight">
            {heroWords.map((word, i) => (
              <span key={i} className="inline-block overflow-hidden mr-[0.25em]">
                <motion.span
                  className="inline-block"
                  initial={{ y: '110%' }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.7, delay: 0.3 + i * 0.1, ease: [0.33, 1, 0.68, 1] }}
                >
                  {word}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            className="text-lg md:text-xl text-soft-white mb-12 max-w-2xl leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
          >
            We transform raw infrastructure into refined business intelligence through the art of data gastronomy.
          </motion.p>

          <motion.div
            className="flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.05 }}
          >
            <Link to="/contact">
              <Button variant="outline" className="bg-white text-black hover:bg-soft-white border-none px-8 py-6 text-sm tracking-wider uppercase transition-all duration-300">
                Book a Consultation
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="border-cloud hover:bg-cloud hover:text-black px-8 py-6 text-sm tracking-wider uppercase transition-all duration-300">
                View Services
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* The Process Section */}
      <section className="px-8 md:px-16 lg:px-24 py-32">
        <FadeInUp>
          <p className="text-xs tracking-[0.3em] uppercase text-silver mb-4">The Process</p>
          <h2 className="text-4xl md:text-5xl font-light mb-16">
            From Raw Data <br className="hidden md:block" />to Refined Insight
          </h2>
        </FadeInUp>
        <div className="grid md:grid-cols-3 gap-12 md:gap-16">
          {['source', 'prep', 'serve'].map((word, i) => (
            <FadeInUp key={word} delay={i * 0.12}>
              <div className="group">
                <p className="text-silver mb-4">0{i + 1}.</p>
                <h3 className="text-4xl md:text-5xl font-light mb-4 group-hover:text-soft-white transition-colors duration-300">{word}</h3>
                <p className="text-xs tracking-[0.3em] uppercase text-silver mb-6">
                  {['Data Ingestion', 'Transformation', 'Business Value'][i]}
                </p>
                <p className="text-soft-white leading-relaxed">
                  {[
                    'Securing the finest raw data from cloud, edge, and legacy ecosystems with absolute integrity.',
                    'Modelling, governance, and structural engineering to ensure every byte is ready for analysis.',
                    'Deploying AI/ML models and strategic dashboards that deliver actionable enterprise intelligence.',
                  ][i]}
                </p>
              </div>
            </FadeInUp>
          ))}
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="px-8 md:px-16 lg:px-24 py-32">
        <FadeInUp>
          <p className="text-xs tracking-[0.3em] uppercase text-silver mb-4">Capabilities</p>
          <h2 className="text-4xl md:text-5xl font-light mb-8">Expertise at Scale</h2>
          <p className="text-lg text-soft-white max-w-4xl leading-relaxed mb-16">
            Whether you're modernising a legacy warehouse or building cloud-native from scratch, we work across the full data stack — from ingestion to insight — with the technical depth enterprise problems demand.
          </p>
        </FadeInUp>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {capabilities.map((capability, index) => (
            <FadeInUp key={index} delay={index * 0.08}>
              <Card className="bg-[#141414] border-outline p-8 hover:border-gray-600 transition-all duration-300 group text-white h-full">
                <div className="text-silver mb-6 group-hover:text-white transition-colors duration-300">
                  {capability.icon}
                </div>
                <h3 className="text-xl font-normal mb-3">{capability.title}</h3>
                <p className="text-silver leading-relaxed">{capability.description}</p>
              </Card>
            </FadeInUp>
          ))}
        </div>
        <FadeInUp>
          <div className="text-center">
            <Link to="/services">
              <Button variant="outline" className="border-cloud hover:bg-cloud hover:text-black px-8 py-6 text-sm tracking-wider uppercase transition-all duration-300">
                View All Services
              </Button>
            </Link>
          </div>
        </FadeInUp>
      </section>

      {/* Why Data Kitchen Section */}
      <section className="px-8 md:px-16 lg:px-24 py-32">
        <FadeInUp>
          <p className="text-xs tracking-[0.3em] uppercase text-silver mb-4">Why Data Kitchen</p>
          <h2 className="text-4xl md:text-5xl font-light mb-16">
            The confidence of enterprise. <br className="hidden md:block" />The agility of a startup.
          </h2>
        </FadeInUp>
        <div className="grid md:grid-cols-3 gap-12 mb-20">
          {whyPoints.map((point, index) => (
            <FadeInUp key={index} delay={index * 0.12}>
              <h4 className="text-xl font-normal mb-4">{point.title}</h4>
              <p className="text-silver leading-relaxed">{point.description}</p>
            </FadeInUp>
          ))}
        </div>

        {/* Animated counters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {stats.map((stat, index) => (
            <FadeInUp key={index} delay={index * 0.1}>
              <div className="text-center">
                <p className="text-4xl md:text-5xl font-light mb-2">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </p>
                <p className="text-sm text-silver">{stat.label}</p>
              </div>
            </FadeInUp>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-8 md:px-16 lg:px-24 py-32">
        <FadeInUp>
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xs tracking-[0.3em] uppercase text-silver mb-8">Ready to Transform</p>
            <h2 className="text-4xl md:text-5xl font-light mb-8">
              Let's architect your <br className="hidden md:block" />data future together.
            </h2>
            <p className="text-lg text-soft-white mb-12">
              Book a complimentary 30-minute consultation to discuss your enterprise data challenges.
            </p>
            <Link to="/contact">
              <Button variant="outline" className="bg-white text-black hover:bg-soft-white border-none px-8 py-6 text-sm tracking-wider uppercase transition-all duration-300">
                Book a Consultation
              </Button>
            </Link>
          </div>
        </FadeInUp>
      </section>

      {/* Footer */}
      <footer className="px-8 md:px-16 lg:px-24 py-12 border-t border-outline">
        <div className="flex items-center justify-between">
          <p className="text-silver text-sm">
            © {new Date().getFullYear()} Data Kitchen. All rights reserved.
          </p>
          <a
            href="https://www.linkedin.com/company/datakitchen-au/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-silver hover:text-white transition-colors duration-300"
            aria-label="LinkedIn"
          >
            <LinkedInIcon />
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Home;
